'use client'

import { useEffect, useRef } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useAuth } from './AuthProvider'

const AFFILIATE_STORAGE_KEY = 'cvcircuit:affiliateCode'
const AFFILIATE_EXPIRY_KEY = 'cvcircuit:affiliateExpiry'

// Silently applies any pending affiliate code from localStorage as soon as the
// user authenticates — on any page, not just /pricing. Runs once per user session.
export default function AffiliateAutoApply() {
  const { user, refreshUser } = useAuth()
  const appliedForUser = useRef<string | null>(null)

  useEffect(() => {
    if (!user) return
    // Don't apply codes to already-subscribed users
    if (user.subscriptionTier === 'subscriber') return
    // Only run once per logged-in user
    if (appliedForUser.current === user.userId) return

    const storedCode = (localStorage.getItem(AFFILIATE_STORAGE_KEY) || '').trim().toUpperCase()
    if (!storedCode) return

    // Clear expired codes
    const storedExpiry = parseInt(localStorage.getItem(AFFILIATE_EXPIRY_KEY) || '0')
    if (storedExpiry > 0 && Date.now() > storedExpiry) {
      localStorage.removeItem(AFFILIATE_STORAGE_KEY)
      localStorage.removeItem(AFFILIATE_EXPIRY_KEY)
      return
    }

    // Already applied this exact code — just clean up localStorage
    if (user.appliedAffiliateCode === storedCode) {
      localStorage.removeItem(AFFILIATE_STORAGE_KEY)
      localStorage.removeItem(AFFILIATE_EXPIRY_KEY)
      return
    }

    appliedForUser.current = user.userId

    void (async () => {
      try {
        const session = await fetchAuthSession()
        const token = session.tokens?.idToken?.toString()
        if (!token) return

        const apiBase = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/+$/, '')
        const res = await fetch(`${apiBase}/billing/apply-affiliate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ code: storedCode, fromLink: true }),
        })

        // Clear localStorage regardless — valid codes get applied, invalid ones get dropped
        localStorage.removeItem(AFFILIATE_STORAGE_KEY)
        localStorage.removeItem(AFFILIATE_EXPIRY_KEY)

        if (res.ok) {
          // Refresh user so appliedAffiliateCode appears on the pricing page immediately
          await refreshUser()
        }
      } catch {
        // Allow retry on next render if there was a network error
        appliedForUser.current = null
      }
    })()
  }, [user?.userId])

  return null
}
